import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Search, Filter, LoaderCircle, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchLastMonthClaims } from '../../services/analyticsApi';

export const LOAD_ERROR_MESSAGE = 'Claims data could not be loaded. No claims are shown.';

const StatusBadge = ({ status }) => {
    // Muted monochrome palette instead of standard bright status colours
    const statusStyles = {
        Approved: 'bg-primary text-white border border-primary',
        Pending: 'bg-transparent text-text-muted border border-line-strong',
        Rejected: 'bg-transparent text-danger border border-danger',
    };
    return (
        <span className={`px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-none ${statusStyles[status] || 'bg-transparent border border-line text-text-muted'}`}>
            {status}
        </span>
    );
};

const InteractiveTable = () => {
    const [claimsData, setClaimsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'repair_date', direction: 'descending' });
    const [activeFilters, setActiveFilters] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // On a failed load the table shows an error with a retry - never placeholder rows that could pass for data.
    const fetchClaims = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetchLastMonthClaims();
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (!Array.isArray(data?.data)) throw new Error("Unexpected response");
            const dataWithIds = data.data.map((item, index) => ({ ...item, id: item.vincd + '-' + index }));
            setClaimsData(dataWithIds);
        } catch (e) {
            console.warn("Claims data could not be loaded:", e);
            setClaimsData([]);
            setError(LOAD_ERROR_MESSAGE);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClaims();
    }, [fetchClaims]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeFilters]);

    const processedData = useMemo(() => {
        let sortableItems = [...claimsData];
        if (searchTerm) {
            sortableItems = sortableItems.filter(item =>
                Object.values(item).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        if (activeFilters.length > 0) {
            sortableItems = sortableItems.filter(item => activeFilters.includes(item.status));
        }
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [claimsData, searchTerm, sortConfig, activeFilters]);

    // Pagination Logic
    const totalPages = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedData.slice(startIndex, startIndex + itemsPerPage);
    }, [processedData, currentPage]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const headers = [
        { key: 'vincd', label: 'VIN' },
        { key: 'model', label: 'Model' },
        { key: 'claimAmount', label: 'Claim Amount ($)' },
        { key: 'status', label: 'Status' },
        { key: 'repair_date', label: 'Repair Date' },
    ];

    // Generate page numbers for pagination controls
    const getPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        
        if (end - start < 4) {
            start = Math.max(1, end - 4);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="w-full bg-white border border-line rounded-none shadow-none">
            {/* Table Controls (Dark Tier) */}
            <div className="p-6 bg-primary text-white flex flex-col sm:flex-row justify-between items-center gap-4 rounded-none">
                <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <h4 className="font-light text-xl tracking-[0.15em] uppercase">CLAIMS DATA</h4>
                </div>
                <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0">
                        <input
                            type="text"
                            placeholder="SEARCH..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-transparent border border-primary-light rounded-none text-white text-xs uppercase tracking-widest focus:outline-none focus:border-white w-full transition-colors placeholder:text-text-muted"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                    </div>
                    <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="border border-primary-light hover:border-white hover:bg-white/10 p-2 rounded-none transition-colors">
                        <Filter className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Filter Dropdown */}
            <AnimatePresence>
                {isFilterOpen && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="bg-background overflow-hidden border-b border-line">
                        <div className="p-6 flex gap-8">
                            {['Approved', 'Pending', 'Rejected'].map(status => (
                                <label key={status} className="flex items-center space-x-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={activeFilters.includes(status)} 
                                        onChange={() => setActiveFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status])} 
                                        className="rounded-none border-line-strong text-primary focus:ring-0 focus:ring-offset-0 bg-transparent h-4 w-4 cursor-pointer" 
                                    />
                                    <span className="text-xs font-semibold tracking-widest uppercase text-primary">{status}</span>
                                </label>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Table Content */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-[#FFFFFF] border-b-2 border-primary">
                        <tr>
                            <th className="px-6 py-4 w-12">
                                <input type="checkbox" className="rounded-none border-line-strong text-primary focus:ring-0 bg-transparent cursor-pointer" />
                            </th>
                            {headers.map(header => (
                                <th key={header.key} className="px-6 py-4 text-xs font-semibold text-primary uppercase tracking-[0.15em] cursor-pointer hover:bg-background transition-colors" onClick={() => requestSort(header.key)}>
                                    <div className="flex items-center space-x-2">
                                        <span>{header.label}</span>
                                        {sortConfig.key === header.key && (sortConfig.direction === 'ascending' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-line">
                        {isLoading ? (
                            <tr><td colSpan={6} className="p-12 text-center text-text-muted uppercase tracking-widest text-sm"><LoaderCircle className="animate-spin h-6 w-6 mx-auto mb-4 text-primary" />LOADING DATA...</td></tr>
                        ) : error ? (
                            <tr><td colSpan={6} className="p-12 text-center">
                                <div role="alert" className="text-danger uppercase tracking-widest text-sm">
                                    <AlertTriangle className="h-6 w-6 mx-auto mb-4" />{error}
                                </div>
                                <button type="button" onClick={fetchClaims} className="mt-6 bg-primary hover:bg-primary-dark text-white text-xs font-semibold py-2 px-6 rounded-none uppercase tracking-[0.15em] transition-colors">
                                    RETRY
                                </button>
                            </td></tr>
                        ) : paginatedData.length === 0 ? (
                            <tr><td colSpan={6} className="p-12 text-center text-text-muted uppercase tracking-widest text-sm">NO RECORDS FOUND.</td></tr>
                        ) : (
                            paginatedData.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-background transition-colors">
                                    <td className="px-6 py-4">
                                        <input type="checkbox" className="rounded-none border-line-strong text-primary focus:ring-0 bg-transparent cursor-pointer" />
                                    </td>
                                    <td className="px-6 py-4 text-sm font-mono text-text-muted">{item.vincd}</td>
                                    <td className="px-6 py-4 text-sm text-primary">{item.model}</td>
                                    <td className="px-6 py-4 text-sm font-medium text-primary">{item.claimAmount}</td>
                                    <td className="px-6 py-4"><StatusBadge status={item.status} /></td>
                                    <td className="px-6 py-4 text-sm text-text-muted">{item.repair_date}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="bg-[#FFFFFF] px-6 py-4 border-t border-line flex items-center justify-between">
                <div className="text-xs text-text-muted tracking-wider uppercase">
                    SHOWING <span className="font-semibold text-primary">{processedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> TO <span className="font-semibold text-primary">{Math.min(currentPage * itemsPerPage, processedData.length)}</span> OF <span className="font-semibold text-primary">{processedData.length}</span> RESULTS
                </div>
                <div className="flex space-x-1">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-none hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-line transition-colors"
                    >
                        <ChevronLeft className="h-4 w-4 text-primary" />
                    </button>
                    
                    {getPageNumbers().map(pageNum => (
                        <button 
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-none text-xs font-semibold tracking-widest transition-colors ${currentPage === pageNum ? 'bg-primary text-white' : 'hover:bg-background text-text-muted border border-transparent hover:border-line'}`}
                        >
                            {pageNum}
                        </button>
                    ))}

                    <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-2 rounded-none hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-line transition-colors"
                    >
                        <ChevronRight className="h-4 w-4 text-primary" />
                    </button>
                </div>
            </div>
            
            {/* Action Buttons Footer */}
            <div className="bg-background p-6 flex flex-wrap justify-center sm:justify-end gap-4 border-t border-line">
                <button className="bg-transparent border border-primary hover:bg-line text-primary text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    RESET
                </button>
                <button className="bg-primary hover:bg-primary-dark border border-primary text-white text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    TRANSFER TO VOR
                </button>
                <button className="bg-primary hover:bg-primary-dark border border-primary text-white text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    GENERATE DATA FILE
                </button>
            </div>
        </div>
    );
};

export default InteractiveTable;